import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../context/AuthContext";
import Header from "../Components/Header/Header";
import Carousel from "../Components/Carousel/Carousel";
import StrapiBlocksRenderer from "../Components/StrapiBlocksRenderer/StrapiBlocksRenderer";
import { API } from "../constant";
import Loading from "../Components/Hooks/Loading";


const IntroScreen = () => {
    const navigate = useNavigate();
    const { user } = useAuthContext();
    const [onetimeScreen, setOnetimeScreen] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [loadingMessage, setLoadingMessage] = useState("Loading intro content...");

    const settings = {navigation:false, pagination:true};
    const MAX_RETRIES = 3;
    const INITIAL_DELAY = 4000; // Wait 5 seconds before first API call

    // Function to handle finish button click
    const handleFinish = () => {
        if (user && user.id) {
            // Mark intro as seen for this user
            localStorage.setItem(`intro_seen_${user.id}`, 'true');
        }
        // Redirect to home/choose option
        navigate("/ChooseOption");
    };

    // Function to fetch data with retry logic
    const fetchIntroData = async (attemptNumber = 0) => {
        try {
            setLoading(true);
            setError(null);

            // Add initial delay on first attempt to give server time to wake up
            if (attemptNumber === 0) {
                console.log(`IntroScreen: Waiting ${INITIAL_DELAY}ms to allow server to wake up...`);
                setLoadingMessage("Preparing content, please wait...");

                // Wait before making the first API call
                await new Promise(resolve => setTimeout(resolve, INITIAL_DELAY));

                console.log("IntroScreen: Making first fetch attempt");
                setLoadingMessage("Loading intro content...");
            } else {
                console.log(`IntroScreen: Retry attempt ${attemptNumber}/${MAX_RETRIES}`);
                setLoadingMessage(`Retrying... (${attemptNumber}/${MAX_RETRIES})`);
            }
            
            const response = await fetch(`${API}/app-information?populate=*`);

            if (!response.ok) {
                throw new Error(`Server error: ${response.status}`);
            }

            const data = await response.json();

            if (data && data.data && data.data.attributes && data.data.attributes.one_time_screen) {
                const ots = data.data.attributes.one_time_screen;
                setOnetimeScreen(ots);
                setLoading(false);
                setRetryCount(0);
            } else {
                throw new Error("Data structure error");
            }

        } catch (error) {
            console.error(`IntroScreen: Error on attempt ${attemptNumber + 1}:`, error.message);

            // If we haven't exceeded max retries, try again with longer delays
            if (attemptNumber < MAX_RETRIES) {
                // More conservative delays: 2s, 4s, 6s
                const delay = Math.min(2000 * (attemptNumber + 1), 6000);
                console.log(`IntroScreen: Retrying in ${delay}ms...`);
                setLoadingMessage(`Server warming up... retrying in ${delay/1000}s`);

                setTimeout(() => {
                    setRetryCount(attemptNumber + 1);
                    fetchIntroData(attemptNumber + 1);
                }, delay);
            } else {
                // Max retries exceeded
                console.error("IntroScreen: Max retries exceeded");
                setError(`Unable to load intro content after ${MAX_RETRIES} attempts. The server may be temporarily unavailable.`);
                setLoading(false);
            }
        }
    };

    // Manual retry function
    const handleRetry = () => {
        setRetryCount(0);
        fetchIntroData(0);
    };

    useEffect(() => {
        fetchIntroData(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only fetch once on mount
    
    if (loading) {
        return (
            <div className="app-inner ots intro-loader">
                <div className="container">
                    <div className="content-wrap bg-gradient">
                        <div style={{textAlign: 'center', padding: '50px 20px'}}>
                            <Loading />
                            <p style={{marginTop: '30px', fontSize: '16px', color: '#fff'}}>
                                {loadingMessage}
                            </p>
                            {retryCount > 0 && (
                                <p style={{marginTop: '10px', fontSize: '14px', color: '#ccc'}}>
                                    Please wait, this may take a moment...
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="app-inner ots skills">
                <div className="container">
                    <div className="content-wrap bg-gradient">
                        <div className="error-container" style={{textAlign: 'center', padding: '50px 20px'}}>
                            <h2>⚠️ Connection Error</h2>
                            <p style={{marginTop: '20px', marginBottom: '20px'}}>{error}</p>
                            <p style={{fontSize: '14px', color: '#666', marginTop: '20px'}}>
                                The server may need a moment to wake up. Try refreshing or clicking retry.
                            </p>
                            <div style={{display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '30px', flexWrap: 'wrap'}}>
                                <button
                                    className="theme-btn gradient-btn"
                                    onClick={handleRetry}
                                >
                                    Retry Loading
                                </button>
                                <button
                                    className="theme-btn gradient-btn"
                                    onClick={handleFinish}
                                >
                                    Skip to Main Screen
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const carouselItems = () => {
        return onetimeScreen.map((item, index) => { // Added return statement
            return (
                <div className="carousel-item" key={index}> {/* Added key prop */}
                    <div className="carousel-item-inner">
                        <div className="item-content">
                            <h2 className="carousel-item-title">{item.title}</h2>
                            <h4 className="subtitle">{item.sub_title}</h4>
                            <StrapiBlocksRenderer blocks={item.description} />
                        </div>
                    </div>
                </div>
            );
        });
    };

    // Debug: Check if we have data
    if (!onetimeScreen || onetimeScreen.length === 0) {
        return (
            <div className="app-inner ots skills">
                <div className="container">
                    <div className="content-wrap bg-gradient">
                        <div className="error-container" style={{textAlign: 'center', padding: '50px 20px'}}>
                            <h2>⚠️ No Content Available</h2>
                            <p style={{marginTop: '20px'}}>The intro screen has no slides configured.</p>
                            <p style={{fontSize: '14px', color: '#666', marginTop: '10px'}}>
                                Check console for data structure details.
                            </p>
                            <button
                                className="theme-btn gradient-btn"
                                style={{marginTop: '30px'}}
                                onClick={handleFinish}
                            >
                                Continue to Main Screen
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className="app-inner ots intro-screen skills">
            <div className="container">
                <div className="content-wrap bg-gradient">
                    <Carousel Items={carouselItems()} Settings={settings} onFinish={handleFinish} finishButtonText="Next" />
                </div>
            </div>
        </div>
    );
};

export default IntroScreen;